import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import BarChartIcon from '@mui/icons-material/BarChart';
import { getAuth } from 'firebase/auth';
import CumulativeReturnChart from './CumulativeReturnChart';
import ReturnsLineChart from './ReturnsLineChart';
import AccuracyLineChart from './AccuracyLineChart';
import DistributionChart from './DistributionChart';
import { initializeMockApi } from '../utils/api';
import { formatReturnsCentered, returnsToCentered } from '../utils/returns';
import { getPlaybookById, mockPlaybooksV2 } from '../data/mockPlaybooksV2';
import { useSiteContent } from '../contexts/SiteContentContext';
import { PLAYBOOK_TYPES } from '../types/picks';
import CustomPlaybookDetails from './CustomPlaybookDetails';
import { savedPick } from '../utils/builder';

// Only use mock data in development and if explicitly enabled
const USE_MOCK_DATA = process.env.NODE_ENV !== 'production' 
  && process.env.REACT_APP_USE_MOCK_DATA === 'true';

// Initialize mock API on module load
if (USE_MOCK_DATA) {
  initializeMockApi(mockPlaybooksV2);
}

const ODIKAI_API_BASE = process.env.REACT_APP_ODIKAI_API_BASE || '/api/odikai';

export default function PlaybookView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const playbookDescriptions = useSiteContent().content.playbook_descriptions;
  const [playbook, setPlaybook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    strategyWeights: false,
    recommendedPicks: false,
    charts: false
  });
  const [isTogglingVisibility, setIsTogglingVisibility] = useState(false);
  const [showVisibilityConfirm, setShowVisibilityConfirm] = useState(false);

  useEffect(() => {
    async function fetchPlaybook() {
      if (!id) return;

      try {
        if (USE_MOCK_DATA) {
          // Try mock data first
          const mockPlaybook = getPlaybookById(id);
          if (mockPlaybook) {
            setPlaybook(mockPlaybook);
            setLoading(false);
            return;
          }
        }

        // Query unified playbooks collection
        const playbookRef = doc(db, 'playbooks', id);
        const playbookDoc = await getDoc(playbookRef);

        if (playbookDoc.exists()) {
          const data = playbookDoc.data();
          
          // Check access: public playbooks are accessible to all, private playbooks only to owner
          const isPublic = data.is_public === true;
          const isOwner = user && data.user_id === user.uid;
          const isPrebuilt = data.is_prebuilt === true;
          
          // Allow access if: public, or user owns it, or it's a prebuilt playbook (which are public)
          if (isPublic || isOwner || isPrebuilt) {
            setPlaybook({ id: playbookDoc.id, ...data });
            setLoading(false);
            return;
          } else {
            setError('Playbook not accessible - you do not have permission to view this playbook');
            setLoading(false);
            return;
          }
        }

        // Document doesn't exist - provide helpful error message
        setError(`Playbook not found: ${id}. The playbook may not exist in the database or may have been deleted.`);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error loading playbook:', err);
        }
        setError('Failed to load playbook. Please try again.');
      } finally {
        setLoading(false);
      }
    }

    fetchPlaybook();
  }, [id, user]);

  const formatDate = (date) => {
    if (!date) return 'N/A';
    // Create date object and adjust for timezone
    const dateObj = new Date(date);
    const timezoneOffset = dateObj.getTimezoneOffset() * 60000; // Convert minutes to milliseconds
    const adjustedDate = new Date(dateObj.getTime() + timezoneOffset);
    
    return adjustedDate.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: '2-digit',
      timeZone: 'UTC' // Force UTC to prevent double timezone conversion
    });
  };

  const formatPercent = (value) => {
    if (!value && value !== 0) return 'N/A';
    return `${(value * 100).toFixed(2)}%`;
  };

  // Error function approximation (Abramowitz and Stegun)
  const erf = (x) => {
    const sign = x >= 0 ? 1 : -1;
    x = Math.abs(x);

    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;

    const t = 1 / (1 + p * x);
    const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

    return sign * y;
  };

  // Cumulative distribution function for normal distribution
  const normalCDF = (x, mean, std) => {
    return 0.5 * (1 + erf((x - mean) / (std * Math.sqrt(2))));
  };

  // Calculate probability that value is greater than threshold
  const probGreaterThan = (threshold, mean, std) => {
    if (!mean || !std || std <= 0) return null;
    return 1 - normalCDF(threshold, mean, std);
  };

  // Handler to navigate to picks page with playbook selected
  const handleViewPicks = () => {
    if (!playbook) return;
    
    // Extract playbook type from ID (e.g., odikai_drake -> drake)
    // For Odikai playbooks, the format is odikai_{type}
    if (playbook.id && playbook.id.startsWith('odikai_')) {
      const playbookType = playbook.id.replace('odikai_', '');
      navigate(`/picks?playbook=${playbookType}`);
    } else {
      // For other playbooks, just navigate to picks page
      navigate('/picks');
    }
  };



  const handleRename = async () => {
    if (!newName.trim()) return;
    
    try {
      const playbookRef = doc(db, 'playbooks', id);
      await updateDoc(playbookRef, {
        name: newName.trim(),
        last_updated: new Date().toISOString()
      });
      
      setPlaybook({
        ...playbook,
        name: newName.trim(),
        last_updated: new Date().toISOString()
      });
      setIsRenaming(false);
    } catch (err) {
      setError('Failed to rename playbook');
    }
  };

  // Whether the daily job picks this custom playbook's bets for each new card (owner only)
  const handleAutoPicksChange = async (value) => {
    try {
      await updateDoc(doc(db, 'playbooks', id), { auto_picks: value });
      setPlaybook((prev) => ({ ...prev, auto_picks: value }));
    } catch (err) {
      setError('Failed to change automatic picks');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) {
      setDeleteConfirm(true);
      return;
    }

    try {
      await deleteDoc(doc(db, 'playbooks', id));
      navigate('/playbooks');
    } catch (err) {
      setError('Failed to delete playbook');
    }
  };

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      const auth = getAuth();
      const currentUser = auth.currentUser;
      if (!currentUser) {
        navigate('/login');
        return;
      }
      const idToken = await currentUser.getIdToken(true); // force refresh
      const response = await fetch(`${ODIKAI_API_BASE}/picks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          portfolio: playbook.portfolio
        })
      });
      if (!response.ok) {
        throw new Error('Failed to refresh playbook');
      }
      const data = await response.json();
      // Update the playbook with new data
      const playbookRef = doc(db, 'playbooks', id);
      await updateDoc(playbookRef, {
        last_updated: new Date().toISOString(),
        event_name: data.event_name,
        event_date: data.event_date,
        picks: data.picks.map(pick => savedPick(pick, data.event_name, data.event_date)),
      });
      setPlaybook(prev => ({
        ...prev,
        last_updated: new Date().toISOString(),
        event_name: data.event_name,
        event_date: data.event_date,
        picks: data.picks.map(pick => savedPick(pick, data.event_name, data.event_date)),
      }));
    } catch (error) {
      setError('Failed to refresh playbook');
    } finally {
      setIsRefreshing(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleToggleVisibility = async () => {
    if (!playbook || !user) return;

    // If making public, show confirmation
    if (!playbook.is_public && !showVisibilityConfirm) {
      setShowVisibilityConfirm(true);
      return;
    }

    setIsTogglingVisibility(true);
    try {
      const newVisibility = !playbook.is_public;
      const playbookRef = doc(db, 'playbooks', id);
      await updateDoc(playbookRef, {
        is_public: newVisibility,
        last_updated: new Date().toISOString()
      });
      
      // Update local state
      setPlaybook(prev => ({
        ...prev,
        is_public: newVisibility,
        last_updated: new Date().toISOString()
      }));

      setShowVisibilityConfirm(false);
    } catch (err) {
      setError('Failed to update playbook visibility: ' + err.message);
    } finally {
      setIsTogglingVisibility(false);
    }
  };

  // Helper: invert STRATEGY_MAPPINGS for display

  if (loading) {
    return (
      <div className="simple-builder-section">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <h2>Loading playbook…</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="simple-builder-section">
        <div className="error-message">{error}</div>
        <button
          className="nav-button back-button"
          onClick={() => navigate('/playbooks')}
        >
          Back to my playbooks
        </button>
      </div>
    );
  }

  if (!playbook) return null;

  // Description: prebuilt playbooks use the shared descriptions, user playbooks their own
  let description = null;
  if (playbook.is_prebuilt && playbook.id) {
    if (playbook.id.startsWith('odikai_')) {
      const playbookType = playbook.id.replace('odikai_', '');
      if (Object.values(PLAYBOOK_TYPES).includes(playbookType)) {
        description = playbookDescriptions[playbookType];
      }
    }
  } else if (playbook.description) {
    description = playbook.description;
  }

  // A custom playbook has a record once the daily job has started tracking it (older documents
  // can carry history fields from a migration, which are not its record)
  const tracked = !playbook.is_prebuilt && Boolean(playbook.tracking_started)
    && Array.isArray(playbook.returns_by_event) && playbook.returns_by_event.length > 0;
  const showRecord = playbook.is_prebuilt || tracked;
  const summary = playbook.distributions_summary;
  const toneOf = (isGood) => (isGood ? 'positive' : 'negative');

  return (
    <div className="simple-builder-section">
      <div className="results-view">
        <div className="picks-display">
          <div className="section-header">
            <div className="header-content">
              <div className="title-section">
                {isRenaming ? (
                  <div className="rename-input-group">
                    <label className="field-label" htmlFor="rename-playbook">Playbook name</label>
                    <input
                      id="rename-playbook"
                      type="text"
                      value={newName}
                      maxLength={100}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="Enter new name"
                      className="rename-input"
                      autoFocus
                    />
                    <div className="rename-actions">
                      <button
                        className="rename-button save"
                        onClick={handleRename}
                        disabled={!newName.trim()}
                      >
                        Save name
                      </button>
                      <button
                        className="rename-button cancel"
                        onClick={() => {
                          setIsRenaming(false);
                          setNewName('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="playbook-kicker">
                      {playbook.is_prebuilt ? (
                        <span className="tag tag-purple">Odikai playbook</span>
                      ) : (
                        <span className="tag">{playbook.is_public ? 'Public' : 'Private'}</span>
                      )}
                    </div>
                    <h2>{playbook.name}</h2>
                    {description && <p className="playbook-description">{description}</p>}
                  </div>
                )}
              </div>
              <div className="header-actions">
                {/* Show View Picks button for prebuilt playbooks */}
                {playbook.is_prebuilt && (
                  <button
                    className="action-button view-picks"
                    onClick={handleViewPicks}
                  >
                    <BarChartIcon /> View picks
                  </button>
                )}
                {/* Only show action buttons for user-owned playbooks (not prebuilt) */}
                {!playbook.is_prebuilt && user && playbook.user_id === user.uid && (
                  <>
                    <button
                      className="action-button refresh"
                      onClick={handleRefresh}
                      disabled={isRefreshing}
                    >
                      {isRefreshing ? (
                        <>
                          <CircularProgress size={18} color="inherit" /> Refreshing…
                        </>
                      ) : (
                        <>
                          <RefreshIcon /> Refresh picks
                        </>
                      )}
                    </button>
                    <button
                      className="action-button rename"
                      onClick={() => {
                        setNewName(playbook.name);
                        setIsRenaming(true);
                      }}
                      disabled={isRefreshing}
                    >
                      <EditIcon /> Rename
                    </button>
                    <button
                      className={`action-button visibility ${playbook.is_public ? 'public' : 'private'}`}
                      onClick={handleToggleVisibility}
                      disabled={isTogglingVisibility || isRefreshing || playbook.admin_locked === true}
                      title={playbook.admin_locked === true ? "Odikai has locked this playbook's visibility" : undefined}
                    >
                      {isTogglingVisibility ? (
                        <>
                          <CircularProgress size={18} color="inherit" /> Updating…
                        </>
                      ) : playbook.is_public ? (
                        <>
                          <PublicIcon /> Make private
                        </>
                      ) : (
                        <>
                          <LockIcon /> Make public
                        </>
                      )}
                    </button>
                    <button
                      className={`action-button delete ${deleteConfirm ? 'confirm' : ''}`}
                      onClick={handleDelete}
                      disabled={isRefreshing || isTogglingVisibility}
                    >
                      <DeleteIcon />
                      {deleteConfirm ? 'Confirm delete' : 'Delete'}
                    </button>
                  </>
                )}
              </div>
            </div>
            {showVisibilityConfirm && (
              <div className="visibility-confirm-dialog" role="dialog" aria-modal="true" aria-labelledby="visibility-title">
                <div className="confirm-dialog-content">
                  <h3 id="visibility-title">Make this playbook public?</h3>
                  <p>Everyone will be able to see it on the Top playbooks page.</p>
                  <div className="confirm-dialog-actions">
                    <button
                      className="cancel-button"
                      onClick={() => setShowVisibilityConfirm(false)}
                      disabled={isTogglingVisibility}
                    >
                      Cancel
                    </button>
                    <button
                      className="confirm-button"
                      onClick={handleToggleVisibility}
                      disabled={isTogglingVisibility}
                    >
                      Make public
                    </button>
                  </div>
                </div>
              </div>
            )}
            {showRecord && (
            <div className="results-summary">
              <div className="summary-item">
                <h3>Average return</h3>
                {summary?.returns_mean !== undefined ? (
                  <>
                    <span className={`summary-value ${toneOf((returnsToCentered(summary.returns_mean) ?? -1) >= 0)}`}>
                      {formatReturnsCentered(summary.returns_mean)}
                    </span>
                    {summary.returns_std !== undefined && (
                      <>
                        <span className="summary-note">±{(summary.returns_std * 100).toFixed(2)}% per event</span>
                        {(() => {
                          const prob = probGreaterThan(1.0, summary.returns_mean, summary.returns_std);
                          return prob !== null ? (
                            <span className="summary-note">
                              Chance an event makes money: <strong>{(prob * 100).toFixed(1)}%</strong>
                            </span>
                          ) : null;
                        })()}
                      </>
                    )}
                  </>
                ) : (
                  <span className={`summary-value ${toneOf((returnsToCentered(playbook.portfolio_returns) ?? -1) >= 0)}`}>
                    {formatReturnsCentered(playbook.portfolio_returns)}
                  </span>
                )}
              </div>
              <div className="summary-item">
                <h3>Average accuracy</h3>
                {summary?.accuracy_mean !== undefined ? (
                  <>
                    <span className={`summary-value ${toneOf(summary.accuracy_mean >= 0.5)}`}>
                      {formatPercent(summary.accuracy_mean)}
                    </span>
                    {summary.accuracy_std !== undefined && (
                      <>
                        <span className="summary-note">±{formatPercent(summary.accuracy_std)} per event</span>
                        {(() => {
                          const prob = probGreaterThan(0.5, summary.accuracy_mean, summary.accuracy_std);
                          return prob !== null ? (
                            <span className="summary-note">
                              Chance of winning over half the picks: <strong>{(prob * 100).toFixed(1)}%</strong>
                            </span>
                          ) : null;
                        })()}
                      </>
                    )}
                  </>
                ) : (
                  <span className="summary-value">N/A</span>
                )}
              </div>
              {playbook.longest_loss_streak !== undefined && (
                <div className="summary-item">
                  <h3>Longest losing streak</h3>
                  <span className={`summary-value ${playbook.longest_loss_streak >= 3 ? 'negative' : ''}`}>
                    {playbook.longest_loss_streak} {playbook.longest_loss_streak === 1 ? 'loss' : 'losses'}
                  </span>
                </div>
              )}
              {playbook.longest_win_streak !== undefined && (
                <div className="summary-item">
                  <h3>Longest win streak</h3>
                  <span className={`summary-value ${playbook.longest_win_streak >= 3 ? 'positive' : ''}`}>
                    {playbook.longest_win_streak} {playbook.longest_win_streak === 1 ? 'win' : 'wins'}
                  </span>
                  {playbook.current_win_streak !== undefined && (
                    <span className="summary-note">Current streak: <strong>{playbook.current_win_streak}</strong></span>
                  )}
                </div>
              )}
            </div>
            )}
          </div>
          {!playbook.is_prebuilt && (
            <CustomPlaybookDetails
              playbook={playbook}
              tracked={tracked}
              canEdit={Boolean(user && playbook.user_id === user.uid)}
              onAutoPicksChange={handleAutoPicksChange}
            />
          )}
          <div className="accordion-sections">
            {showRecord && (playbook.returns_by_event || playbook.accuracy_by_event || playbook.distributions_summary) && (
              <div className="accordion-item">
                <button
                  className="accordion-header"
                  onClick={() => toggleSection('charts')}
                  aria-expanded={Boolean(expandedSections.charts)}
                >
                  <h3>Performance charts</h3>
                  {expandedSections.charts ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </button>
                {expandedSections.charts && (
                  <div className="accordion-content charts-content">
                    {playbook.returns_by_event && playbook.returns_by_event.length > 0 && (
                      <CumulativeReturnChart
                        returnsByEvent={playbook.returns_by_event}
                        trainingDataCutoffDate={playbook.training_data_cutoff_date}
                        eventDates={playbook.included_event_dates}
                      />
                    )}
                    {playbook.returns_by_event && playbook.returns_by_event.length > 0 && (
                      <ReturnsLineChart
                        returnsByEvent={playbook.returns_by_event}
                        lastEventReturn={playbook.last_event_return}
                        trainingDataCutoffDate={playbook.training_data_cutoff_date}
                        eventDates={playbook.included_event_dates}
                      />
                    )}
                    {playbook.accuracy_by_event && playbook.accuracy_by_event.length > 0 && (
                      <AccuracyLineChart
                        accuracyByEvent={playbook.accuracy_by_event}
                        accuracyPctLastEvent={playbook.accuracy_pct_last_event}
                        trainingDataCutoffDate={playbook.training_data_cutoff_date}
                        eventDates={playbook.included_event_dates}
                      />
                    )}
                    {summary && (
                      <div className="chart-grid">
                        {summary.returns_mean !== undefined && (
                          <DistributionChart
                            mean={returnsToCentered(summary.returns_mean)}
                            stdDev={(summary.returns_std ?? 0) * 100}
                            currentValue={returnsToCentered(playbook.last_event_return)}
                            title="Returns distribution"
                            xAxisLabel="Return"
                            minValue={(returnsToCentered(summary.returns_mean) ?? 0) - 4 * (summary.returns_std ?? 0) * 100}
                            maxValue={(returnsToCentered(summary.returns_mean) ?? 0) + 4 * (summary.returns_std ?? 0) * 100}
                          />
                        )}
                        {summary.accuracy_mean !== undefined && (
                          <DistributionChart
                            mean={summary.accuracy_mean}
                            stdDev={summary.accuracy_std}
                            currentValue={playbook.accuracy_pct_last_event}
                            title="Accuracy distribution"
                            xAxisLabel="Accuracy"
                            minValue={0}
                            maxValue={1}
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="page-back">
          <button className="nav-button back-button" onClick={() => navigate('/playbooks')}>
            Back to my playbooks
          </button>
        </div>
      </div>
    </div>
  );
}
